import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { ContentSegment } from '@/types/editor.types';
import { useEditorStore } from '@/stores/editor.store';

/**
 */
interface SaveQueueDB extends DBSchema {
  'save-queue': {
    key: string; // topicNodeId
    value: {
      topicNodeId: string;
      segments: ContentSegment[];
      timestamp: number;
      retryCount: number;
      lastError?: string;
    };
    indexes: {
      'by-timestamp': number;
    };
  };
  'auth': {
    key: string;
    value: {
      key: string;
      value: string;
    };
  };
}

/**
 */
class SaveQueueService {
  private db: IDBPDatabase<SaveQueueDB> | null = null;
  private readonly DB_NAME = 'careertree-save-queue';
  private readonly DB_VERSION = 1;

  /**
   */
  async init(): Promise<void> {
    if (this.db) return;

    try {
      this.db = await openDB<SaveQueueDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db) {
          // save-queue store
          if (!db.objectStoreNames.contains('save-queue')) {
            const store = db.createObjectStore('save-queue', {
              keyPath: 'topicNodeId',
            });
            store.createIndex('by-timestamp', 'timestamp');
          }

          // auth store
          if (!db.objectStoreNames.contains('auth')) {
            db.createObjectStore('auth', { keyPath: 'key' });
          }
        },
      });

      console.log('📦 SaveQueue DB initialized');

      await this.syncFromIndexedDB();
    } catch (error) {
      console.error('DB initialization failed:', error);
      throw error;
    }
  }

  /**
   */
  private async syncFromIndexedDB(): Promise<void> {
    if (!this.db) return;

    try {
      const items = await this.db.getAll('save-queue');

      const store = useEditorStore.getState();
      items.forEach(item => {
        store.addToSaveQueue(item.topicNodeId, item.segments);
      });

      console.log(`🔄 Restored ${items.length} items from IndexedDB`);
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }

  /**
   */
  async enqueue(topicNodeId: string, segments: ContentSegment[]): Promise<void> {
    await this.init();

    const item = {
      topicNodeId,
      segments,
      timestamp: Date.now(),
      retryCount: 0,
    };

    try {
      await this.db!.put('save-queue', item);

      useEditorStore.getState().addToSaveQueue(topicNodeId, segments);

      console.log(`📥 Added to queue: ${topicNodeId}`);

      this.triggerProcessing();
    } catch (error) {
      console.error('Queue add failed:', error);
      throw error;
    }
  }

  /**
   */
  async dequeue(topicNodeId: string): Promise<void> {
    await this.init();

    try {
      await this.db!.delete('save-queue', topicNodeId);

      useEditorStore.getState().removeFromSaveQueue(topicNodeId);

      console.log(`📤 Removed from queue: ${topicNodeId}`);
    } catch (error) {
      console.error('Queue removal failed:', error);
      throw error;
    }
  }

  /**
   */
  getPendingItems() {
    return useEditorStore.getState().getAllPendingItems();
  }

  /**
   */
  async incrementRetry(topicNodeId: string, error?: string): Promise<void> {
    await this.init();

    try {
      useEditorStore.getState().incrementRetry(topicNodeId);

      const item = await this.db!.get('save-queue', topicNodeId);
      if (item) {
        item.retryCount++;
        if (error) {
          item.lastError = error;
        }
        await this.db!.put('save-queue', item);
      }

      console.log(`🔄 Retry count increased: ${topicNodeId} (${item?.retryCount})`);
    } catch (err) {
      console.error('Retry count update failed:', err);
    }
  }

  /**
   */
  private triggerProcessing(): void {
    window.dispatchEvent(new CustomEvent('save-queue:process'));
  }

  /**
   */
  async saveToken(token: string): Promise<void> {
    await this.init();

    try {
      await this.db!.put('auth', { key: 'token', value: token });
      console.log('🔑 Token saved');
    } catch (error) {
      console.error('Token save failed:', error);
      throw error;
    }
  }

  /**
   */
  async getToken(): Promise<string | null> {
    await this.init();

    try {
      const result = await this.db!.get('auth', 'token');
      return result?.value || null;
    } catch (error) {
      console.error('Token lookup failed:', error);
      return null;
    }
  }

  /**
   */
  async removeToken(): Promise<void> {
    await this.init();

    try {
      await this.db!.delete('auth', 'token');
      console.log('🔑 Token removed');
    } catch (error) {
      console.error('Token removal failed:', error);
    }
  }
}

export const saveQueueService = new SaveQueueService();
